import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { getFieldId, deduplicateFeatures } from '../utils/fieldUtils';
import { useAuth } from '../contexts/AuthContext';

const SELECTED_CVRS_KEY = 'fieldvision_selectedCVRs';

// Simple function to generate SVG path from GeoJSON coordinates
const getFieldThumbnail = (geometry: any) => {
  if (!geometry || !geometry.coordinates || !geometry.coordinates[0]) return null;

  const coords = geometry.coordinates[0];

  // Find bounding box
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  coords.forEach((coord: number[]) => {
    minX = Math.min(minX, coord[0]);
    maxX = Math.max(maxX, coord[0]);
    minY = Math.min(minY, coord[1]);
    maxY = Math.max(maxY, coord[1]);
  });

  // Scale to fit in 40x40 viewBox
  const width = maxX - minX;
  const height = maxY - minY;
  const scale = 35 / Math.max(width, height);

  // Generate SVG path
  const path = coords.map((coord: number[], i: number) => {
    const x = (coord[0] - minX) * scale + 2.5;
    const y = 40 - ((coord[1] - minY) * scale + 2.5); // Flip Y
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ') + ' Z';

  return path;
};

/**
 * Deduplicate fields array by id (CVR-Marknr).
 * Keeps the first occurrence of each ID.
 */
const deduplicateFields = (fields: any[]): any[] => {
  const seen = new Map<string, any>();
  for (const field of fields) {
    const id = field.id;
    if (id && !seen.has(id)) {
      seen.set(id, field);
    }
  }
  return Array.from(seen.values());
};

interface MyFieldsProps {
  onFieldsLoaded: (geojson: any, cvrs: string[]) => void;
  selectedFieldIds: string[];
  onSelectedFieldsChange: (fieldIds: string[]) => void;
  zoomLoadedFields: any[];
  onRemoveZoomField: (fieldId: string) => void;
  onNavigateToFieldDetail?: (fieldId: number, fieldData?: any) => void;
}

const MyFields: React.FC<MyFieldsProps> = ({
  onFieldsLoaded,
  selectedFieldIds,
  onSelectedFieldsChange,
  onRemoveZoomField,
  onNavigateToFieldDetail,
  zoomLoadedFields
}) => {
  const [cvrs, setCvrs] = useState<string[]>([]);
  const [selectedCVRs, setSelectedCVRs] = useState<string[]>(() => {
    const stored = localStorage.getItem(SELECTED_CVRS_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [cvrFields, setCvrFields] = useState<Record<string, any[]>>({});
  const [cvrGeojson, setCvrGeojson] = useState<Record<string, any>>({});
  const [newCVR, setNewCVR] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingCVR, setLoadingCVR] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showAddCVR, setShowAddCVR] = useState(false);
  const [deletingCVR, setDeletingCVR] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    loadUserCVRs();
  }, []);

  // Pre-select profile CVR ONLY if it's the only CVR AND no stored selection exists
  useEffect(() => {
    const storedCVRs = localStorage.getItem(SELECTED_CVRS_KEY);
    if (storedCVRs) return; // Don't auto-select if user has saved preferences

    const profileCVR = (user as any)?.cvr;
    if (profileCVR && cvrs.length === 1 && cvrs[0] === profileCVR && !selectedCVRs.includes(profileCVR)) {
      setSelectedCVRs([profileCVR]);
      // Also load fields for this CVR
      if (!cvrFields[profileCVR]) {
        loadFieldsForCVR(profileCVR);
      }
    }
  }, [cvrs, user]);

  // Persist selectedCVRs to localStorage
  useEffect(() => {
    if (selectedCVRs.length > 0) {
      localStorage.setItem(SELECTED_CVRS_KEY, JSON.stringify(selectedCVRs));
    } else {
      localStorage.removeItem(SELECTED_CVRS_KEY);
    }
  }, [selectedCVRs]);

  // Load fields for stored selectedCVRs on mount
  useEffect(() => {
    if (cvrs.length > 0 && selectedCVRs.length > 0) {
      selectedCVRs.forEach(cvr => {
        if (cvrs.includes(cvr) && !cvrFields[cvr]) {
          loadFieldsForCVR(cvr, false);
        }
      });
    }
  }, [cvrs]);

  const loadUserCVRs = async () => {
    try {
      const result = await apiService.getUserCVRs();
      setCvrs(result.cvrs);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadFieldsForCVR = async (cvr: string, autoSelect: boolean = true) => {
    setLoadingCVR(cvr);
    setError('');

    try {
      const geojson = await apiService.searchFields([cvr]);

      if (geojson.features && geojson.features.length > 0) {
        // Deduplicate features using CVR-Marknr as unique key
        const dedupedFeatures = deduplicateFeatures(geojson.features);
        const dedupedGeojson = {
          ...geojson,
          features: dedupedFeatures
        };

        // Store full geojson for this CVR
        setCvrGeojson(prev => ({ ...prev, [cvr]: dedupedGeojson }));

        // Store fields for this CVR - use CVR-Marknr as ID
        const fields = dedupedFeatures.map((feature: any) => ({
          id: getFieldId(feature),  // CVR-Marknr as unique ID
          markblok: feature.properties.MARKBLOKNR,  // Keep for display
          marknr: feature.properties.Marknr,
          areal: feature.properties.GB_AREAL || feature.properties.IMK_areal || feature.properties.GEOMETRISK || 0,
          svgPath: getFieldThumbnail(feature.geometry),
          geometry: feature.geometry
        }));

        // Sort fields by marknr (numeric)
        fields.sort((a: any, b: any) => {
          const aNum = parseInt(a.marknr) || 0;
          const bNum = parseInt(b.marknr) || 0;
          return aNum - bNum;
        });

        setCvrFields(prev => ({ ...prev, [cvr]: fields }));

        // Auto-select all fields from this CVR (only if autoSelect is true)
        if (autoSelect) {
          const fieldIds = fields.map((f: any) => f.id);
          onSelectedFieldsChange(Array.from(new Set(selectedFieldIds.concat(fieldIds))));
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingCVR(null);
    }
  };

  const handleAddCVR = async () => {
    if (!newCVR.trim()) {
      setError('CVR nummer er påkrævet');
      return;
    }

    try {
      await apiService.addUserCVR(newCVR.trim());
      setNewCVR('');
      setShowAddCVR(false);
      setError('');
      loadUserCVRs();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteCVR = async (cvr: string) => {
    if (!window.confirm(`Er du sikker på at du vil fjerne CVR ${cvr}?`)) {
      return;
    }

    setDeletingCVR(cvr);
    try {
      await apiService.deleteUserCVR(cvr);

      // Calculate remaining selected CVRs (excluding deleted one)
      const remainingSelectedCVRs = selectedCVRs.filter(c => c !== cvr);
      setSelectedCVRs(remainingSelectedCVRs);

      // Remove fields from this CVR from selection
      const fieldsToRemove = cvrFields[cvr]?.map(f => f.id) || [];
      onSelectedFieldsChange(selectedFieldIds.filter(id => !fieldsToRemove.includes(id)));

      // FIX: Build new geojson from remaining CVRs and update parent
      const remainingFeatures: any[] = [];
      Object.entries(cvrGeojson).forEach(([cvrKey, geojsonData]) => {
        // Include if it's a remaining selected CVR (not the deleted one)
        if (cvrKey !== cvr && remainingSelectedCVRs.includes(cvrKey)) {
          remainingFeatures.push(...geojsonData.features);
        }
      });

      // Update parent with new geojson (or null if no features left)
      if (remainingFeatures.length > 0) {
        const dedupedFeatures = deduplicateFeatures(remainingFeatures);
        const newGeojson = {
          type: 'FeatureCollection',
          features: dedupedFeatures
        };
        onFieldsLoaded(newGeojson, remainingSelectedCVRs);
      } else {
        // No features left - clear the map
        onFieldsLoaded(null, []);
      }

      // Clean up local state
      setCvrFields(prev => {
        const newFields = { ...prev };
        delete newFields[cvr];
        return newFields;
      });

      setCvrGeojson(prev => {
        const newGeojson = { ...prev };
        delete newGeojson[cvr];
        return newGeojson;
      });

      loadUserCVRs();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingCVR(null);
    }
  };

  const handleToggleCVR = async (cvr: string) => {
    const isCurrentlySelected = selectedCVRs.includes(cvr);

    if (isCurrentlySelected) {
      // Deselect CVR and its fields
      setSelectedCVRs(prev => prev.filter(c => c !== cvr));
      const fieldsToRemove = cvrFields[cvr]?.map(f => f.id) || [];
      onSelectedFieldsChange(selectedFieldIds.filter(id => !fieldsToRemove.includes(id)));
    } else {
      // Select CVR
      setSelectedCVRs(prev => prev.concat([cvr]));
      // Load fields if not already loaded
      if (!cvrFields[cvr]) {
        await loadFieldsForCVR(cvr);
      } else {
        // Fields already loaded, auto-select them
        const fieldIds = cvrFields[cvr].map((f: any) => f.id);
        onSelectedFieldsChange(Array.from(new Set(selectedFieldIds.concat(fieldIds))));
      }
    }
  };

  const handleToggleField = (fieldId: string) => {
    if (selectedFieldIds.includes(fieldId)) {
      onSelectedFieldsChange(selectedFieldIds.filter(id => id !== fieldId));
    } else {
      onSelectedFieldsChange(selectedFieldIds.concat([fieldId]));
    }
  };

  const handleLoadFields = async () => {
    setLoading(true);
    setError('');

    try {
      // Combine geojson from all selected CVRs
      const allFeatures: any[] = [];

      Object.entries(cvrGeojson).forEach(([cvr, geojson]) => {
        if (selectedCVRs.includes(cvr)) {
          // Only include fields that are selected
          const selectedFeatures = geojson.features.filter((feature: any) =>
            selectedFieldIds.includes(getFieldId(feature))
          );
          allFeatures.push(...selectedFeatures);
        }
      });

      if (allFeatures.length === 0) {
        setError('Ingen marker fra valgte CVR');
        return;
      }

      // Deduplicate combined features using CVR-Marknr
      const dedupedFeatures = deduplicateFeatures(allFeatures);

      const combinedGeojson = {
        type: 'FeatureCollection',
        features: dedupedFeatures
      };

      onFieldsLoaded(combinedGeojson, selectedCVRs);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Prepare zoom-loaded fields for display - use CVR-Marknr as ID
  const rawZoomFields = zoomLoadedFields.map(feature => ({
    id: getFieldId(feature),  // CVR-Marknr as unique ID
    markblok: feature.properties.MARKBLOKNR,  // Keep for display
    marknr: feature.properties.Marknr,
    areal: feature.properties.GB_AREAL || feature.properties.IMK_areal || feature.properties.GEOMETRISK || 0,
    svgPath: getFieldThumbnail(feature.geometry),
    cvr: feature.properties.CVR
  }));
  const zoomFields = deduplicateFields(rawZoomFields);

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Mine Marker</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {cvrs.length === 0 ? (
        <div className="mb-6">
          <p className="text-gray-600 mb-4">Du har ikke tilføjet nogen CVR numre endnu.</p>
          <button
            onClick={() => setShowAddCVR(true)}
            className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
          >
            Tilføj CVR nummer
          </button>
        </div>
      ) : (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Dine CVR numre
          </label>
          <div className="space-y-3">
            {cvrs.map((cvr) => {
              const isSelected = selectedCVRs.includes(cvr);
              const fields = cvrFields[cvr] || [];
              const isLoading = loadingCVR === cvr;
              const selectedCount = fields.filter(f => selectedFieldIds.includes(f.id)).length;

              return (
                <div key={cvr} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* CVR Header */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100">
                    <label className="flex items-center flex-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleCVR(cvr)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        disabled={loading || isLoading}
                      />
                      <span className="ml-3 font-medium text-gray-800">CVR: {cvr}</span>
                      {isSelected && fields.length > 0 && (
                        <span className="ml-2 text-sm text-gray-500">
                          ({selectedCount}/{fields.length} marker)
                        </span>
                      )}
                    </label>
                    <button
                      onClick={() => handleDeleteCVR(cvr)}
                      disabled={deletingCVR === cvr}
                      className="ml-3 text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Fjern CVR"
                    >
                      {deletingCVR === cvr ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Field List */}
                  {isSelected && (
                    <div className="p-3 bg-white border-t border-gray-200">
                      {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mr-2"></div>
                          <span className="text-sm text-gray-600">Henter marker...</span>
                        </div>
                      ) : fields.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {fields.map((field) => {
                            const isFieldSelected = selectedFieldIds.includes(field.id);
                            return (
                              <div
                                key={field.id}
                                className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                                  isFieldSelected
                                    ? 'bg-green-50 hover:bg-green-100 border border-green-200'
                                    : 'hover:bg-gray-50 border border-transparent'
                                }`}
                                onClick={() => handleToggleField(field.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={isFieldSelected}
                                  onChange={() => handleToggleField(field.id)}
                                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                  onClick={(e) => e.stopPropagation()}
                                />

                                {field.svgPath && (
                                  <svg width="40" height="40" viewBox="0 0 40 40" className="flex-shrink-0">
                                    <path
                                      d={field.svgPath}
                                      fill={isFieldSelected ? "#4783b6" : "#a8c5de"}
                                      stroke={isFieldSelected ? "#4783b6" : "#4783b6"}
                                      strokeWidth="1"
                                    />
                                  </svg>
                                )}

                                <div className="flex-1 min-w-0">
                                  <div className={`font-medium text-sm ${isFieldSelected ? 'text-green-900' : 'text-gray-900'}`}>
                                    {field.marknr ? `Mark ${field.marknr}` : `Markblok ${field.markblok}`}
                                  </div>
                                  <div className={`text-xs ${isFieldSelected ? 'text-green-700' : 'text-gray-500'}`}>
                                    {field.areal > 1000
                                      ? `${(field.areal / 10000).toFixed(2)} ha`
                                      : `${field.areal.toFixed(2)} ha`
                                    }
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 py-2">Ingen marker fundet</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={() => setShowAddCVR(true)}
            className="w-full mt-3 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200"
            disabled={loading}
          >
            + Tilføj nyt CVR
          </button>
        </div>
      )}

      {/* Zoom-loaded fields section */}
      {zoomFields.length > 0 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Andre valgte marker (fra kort zoom)
          </label>
          <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
            <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
              {zoomFields.map((field) => {
                const isFieldSelected = selectedFieldIds.includes(field.id);
                return (
                  <div
                    key={field.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      isFieldSelected
                        ? 'bg-green-50 hover:bg-green-100 border border-green-200'
                        : 'hover:bg-gray-50 border border-transparent bg-white'
                    }`}
                    onClick={() => handleToggleField(field.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isFieldSelected}
                      onChange={() => handleToggleField(field.id)}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      onClick={(e) => e.stopPropagation()}
                    />

                    {field.svgPath && (
                      <svg width="40" height="40" viewBox="0 0 40 40" className="flex-shrink-0">
                        <path
                          d={field.svgPath}
                          fill={isFieldSelected ? "#a0532a" : "#7b7b7b"}
                          stroke={isFieldSelected ? "#a0532a" : "#7b7b7b"}
                          strokeWidth="1"
                        />
                      </svg>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm ${isFieldSelected ? 'text-green-900' : 'text-gray-900'}`}>
                        {field.marknr ? `Mark ${field.marknr}` : `Markblok ${field.markblok}`}
                      </div>
                      <div className={`text-xs ${isFieldSelected ? 'text-green-700' : 'text-gray-500'}`}>
                        {field.areal > 1000
                          ? `${(field.areal / 10000).toFixed(2)} ha`
                          : `${field.areal.toFixed(2)} ha`
                        } • CVR: {field.cvr || 'N/A'}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveZoomField(field.id);
                      }}
                      className="ml-2 text-red-600 hover:text-red-800"
                      title="Fjern mark"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showAddCVR && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-3">Tilføj CVR nummer</h3>
          <input
            type="text"
            value={newCVR}
            onChange={(e) => setNewCVR(e.target.value)}
            placeholder="Indtast CVR nummer"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent mb-3"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddCVR}
              className="btn-primary flex-1 py-2"
            >
              Tilføj
            </button>
            <button
              onClick={() => {
                setShowAddCVR(false);
                setNewCVR('');
                setError('');
              }}
              className="flex-1 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300"
            >
              Annuller
            </button>
          </div>
        </div>
      )}

      {cvrs.length > 0 && selectedCVRs.length > 0 && (
        <button
          onClick={handleLoadFields}
          disabled={loading}
          className="btn-primary w-full py-3 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Henter marker...' : 'Vis marker på kort'}
        </button>
      )}

      <div className="mt-6 p-3 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-gray-700">
          <strong>Tip:</strong> Zoom ind på kortet for at se marker udenfor dine CVR'er. Klik på marker for at vælge dem til booking.
        </p>
      </div>
    </div>
  );
};

export default MyFields;
